import React from 'react';
import { Trash2 } from 'lucide-react';
import TrafficSourceBadge from './TrafficSourceBadge';

interface ViewOrderModalProps {
  isOpen: boolean;
  order: any;
  onClose: () => void;
  onDelete: (orderId: number) => void;
}

export const ViewOrderModal: React.FC<ViewOrderModalProps> = ({
  isOpen,
  order,
  onClose,
  onDelete,
}) => {
  if (!isOpen || !order) return null;

  const items = order.items || [];
  const itemsSubtotal = items.length > 0
    ? items.reduce((s: number, i: any) => s + parseFloat(i.subtotal || (parseFloat(i.price || 0) * (i.quantity || 1)) || 0), 0)
    : Math.max(0, (parseFloat(order.total_amount || 0) - parseFloat(order.shipping_cost || 0)));
  const shipping = parseFloat(order.shipping_cost) || 0;
  const discount = parseFloat(order.discount_amount) || 0;
  const total = itemsSubtotal + shipping - discount;

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          animation: 'modalSlideIn 0.3s ease-out',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #1e293b, #0f172a)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', letterSpacing: '-0.025em', color: '#f8fafc' }}>
              Order Details
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
              Order #{order.order_number}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '1.1rem',
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div
          className="modal-body-scroll"
          style={{
            padding: '20px 24px',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {/* Top 4-card summary bar */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
            {[
              {
                label: 'Order Date',
                value: order.created_at
                  ? new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : 'N/A',
              },
              {
                label: 'Platform / Source',
                value: (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                    <TrafficSourceBadge order={order} />
                  </div>
                ),
              },
              {
                label: 'Payment',
                value: order.payment_method === 'cash_on_delivery' ? 'Cash on Delivery' : (order.payment_method || 'COD'),
              },
              {
                label: 'Last Updated',
                value: order.updated_at
                  ? new Date(order.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  : 'N/A',
              },
            ].map(card => (
              <div key={card.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                  {card.label}
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: '600', color: '#1e293b' }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* Customer Information */}
          <div>
            <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
              Customer Information
            </h3>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <tbody>
                  {[
                    { label: 'Full Name', value: order.customer_name },
                    { label: 'Phone', value: order.customer_phone || 'N/A' },
                    { label: 'Shipping Address', value: order.shipping_address },
                    {
                      label: 'Delivery Option',
                      value: (
                        <span>
                          <strong>
                            {order.delivery_option_name || (order.is_inside_dhaka ? 'Inside Dhaka' : (parseFloat(order.shipping_cost || 0) > 60 ? 'Outside Dhaka' : 'Standard Delivery'))}
                          </strong>
                          <span style={{ color: '#2563eb', fontWeight: '600', marginLeft: '8px' }}>
                            (৳{parseFloat(order.shipping_cost || 0).toFixed(2)})
                          </span>
                        </span>
                      ),
                    },
                    {
                      label: 'Order Status',
                      value: (
                        <span className={`status-badge ${(order.status || 'pending').toLowerCase()}`} style={{ display: 'inline-block', fontSize: '0.72rem', padding: '3px 10px' }}>
                          {(order.status || 'Pending').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                        </span>
                      ),
                    },
                  ].map((row, i, arr) => (
                    <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                      <td style={{ padding: '9px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                        {row.label}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#1e293b', lineHeight: '1.4' }}>
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Marketing & Ad Attribution */}
          <div>
            <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #8b5cf6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
              🎯 Marketing &amp; Ad Attribution
            </h3>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <tbody>
                  {[
                    {
                      label: 'Traffic Source',
                      value: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TrafficSourceBadge order={order} />
                          <span style={{ fontSize: '11px', color: '#64748b' }}>
                            ({order.traffic_source_display || order.traffic_source || 'Organic'})
                          </span>
                        </div>
                      ),
                    },
                    { label: 'Campaign (utm_campaign)', value: order.utm_campaign || '—' },
                    { label: 'Ad Content (utm_content)', value: order.utm_content || '—' },
                    { label: 'Medium (utm_medium)', value: order.utm_medium || '—' },
                    { label: 'Keywords (utm_term)', value: order.utm_term || '—' },
                    {
                      label: 'Click ID (fbclid / ttclid)',
                      value: order.click_id ? (
                        <code style={{ fontSize: '11px', wordBreak: 'break-all', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                          {order.click_id}
                        </code>
                      ) : '—',
                    },
                    {
                      label: 'Landing Page',
                      value: order.landing_page ? (
                        <span style={{ fontSize: '11px', wordBreak: 'break-all', color: '#2563eb' }}>
                          {order.landing_page}
                        </span>
                      ) : '—',
                    },
                    {
                      label: 'Referrer URL',
                      value: order.referrer_url ? (
                        <span style={{ fontSize: '11px', wordBreak: 'break-all', color: '#64748b' }}>
                          {order.referrer_url}
                        </span>
                      ) : '—',
                    },
                  ].map((row, i, arr) => (
                    <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                      <td style={{ padding: '8px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                        {row.label}
                      </td>
                      <td style={{ padding: '8px 14px', color: '#1e293b', lineHeight: '1.4' }}>
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
              Order Items
            </h3>
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f1f5f9', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>#</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Product</th>
                    <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Variants</th>
                    <th style={{ padding: '9px 14px', textAlign: 'center', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Qty</th>
                    <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Unit Price</th>
                    <th style={{ padding: '9px 14px', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: any, idx: number) => {
                    const color = item.selected_color || item.color || '';
                    const size = item.selected_size || item.size || '';
                    const weight = item.selected_weight || item.weight || '';
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                        <td style={{ padding: '9px 14px', color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>
                        <td style={{ padding: '9px 14px', color: '#1e293b', fontWeight: '500' }}>
                          {item.product_name || item.name || 'Unknown Product'}
                        </td>
                        <td style={{ padding: '9px 14px', color: '#64748b', fontSize: '0.8rem' }}>
                          {(color || size || weight) ? (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                              {color && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: color.toLowerCase() }} />
                                  {color}
                                </span>
                              )}
                              {size && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                  {size}
                                </span>
                              )}
                              {weight && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                                  {weight}
                                </span>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '9px 14px', textAlign: 'center', color: '#1e293b', fontWeight: '600' }}>{item.quantity}</td>
                        <td style={{ padding: '9px 14px', textAlign: 'right', color: '#64748b' }}>
                          <div>৳{parseFloat(item.price || 0).toFixed(2)}</div>
                          {parseFloat(item.buying_price || 0) > 0 && (
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                              Buy: ৳{parseFloat(item.buying_price).toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '9px 14px', textAlign: 'right', color: '#2563eb', fontWeight: '600' }}>
                          <div>৳{parseFloat(item.subtotal || 0).toFixed(2)}</div>
                          {item.item_profit !== undefined && item.item_profit !== null && (
                            <div style={{ fontSize: '0.7rem', color: parseFloat(item.item_profit) >= 0 ? '#16a34a' : '#ef4444', fontWeight: '600' }}>
                              {parseFloat(item.item_profit) >= 0 ? '+' : ''}৳{parseFloat(item.item_profit).toFixed(2)} profit
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Summary */}
          <div>
            <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
              Payment Summary
            </h3>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', maxWidth: '380px', marginLeft: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '7px' }}>
                <span>Items Subtotal</span>
                <span style={{ fontWeight: '500' }}>৳{itemsSubtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#475569', marginBottom: '7px' }}>
                <span>Shipping Charge</span>
                <span style={{ fontWeight: '500' }}>৳{shipping.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', color: '#16a34a', marginBottom: '7px' }}>
                  <span>
                    Discount {order.coupon ? '(Coupon)' : order.admin_discount_type ? `(Admin ${order.admin_discount_type === 'percentage' ? order.admin_discount_value + '%' : ''})` : ''}
                  </span>
                  <span style={{ fontWeight: '600' }}>−৳{discount.toFixed(2)}</span>
                </div>
              )}
              <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '10px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>
                <span>Grand Total (COD)</span>
                <span style={{ color: '#2563eb' }}>৳{total.toFixed(2)}</span>
              </div>
              {order.gross_profit !== undefined && order.gross_profit !== null && (
                <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '10px', marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.84rem', color: '#0f766e', fontWeight: '600' }}>
                    💰 Gross Profit (অর্ডার লাভ)
                  </span>
                  <span style={{
                    color: parseFloat(order.gross_profit || 0) >= 0 ? '#059669' : '#dc2626',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    background: parseFloat(order.gross_profit || 0) >= 0 ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${parseFloat(order.gross_profit || 0) >= 0 ? '#a7f3d0' : '#fecaca'}`,
                    padding: '2px 8px',
                    borderRadius: '6px',
                  }}>
                    {parseFloat(order.gross_profit || 0) >= 0 ? '+' : ''}৳{parseFloat(order.gross_profit || 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Steadfast Courier Info */}
          {order.steadfast_consignment_id && (
            <div>
              <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                Courier / Shipping
              </h3>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                  <tbody>
                    {[
                      { label: 'Consignment ID', value: order.steadfast_consignment_id },
                      { label: 'Tracking Code', value: <span style={{ color: '#2563eb', fontWeight: '600' }}>{order.steadfast_tracking_code}</span> },
                      { label: 'Courier Status', value: <span style={{ padding: '3px 10px', borderRadius: '5px', backgroundColor: '#dcfce7', color: '#15803d', fontSize: '0.75rem', fontWeight: '600' }}>{order.steadfast_status || 'In Transit'}</span> },
                      ...(order.steadfast_sent_at ? [{ label: 'Sent to Courier', value: new Date(order.steadfast_sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }] : []),
                    ].map((row, i, arr) => (
                      <tr key={row.label} style={{ borderBottom: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none', backgroundColor: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                        <td style={{ padding: '9px 14px', fontWeight: '600', color: '#64748b', width: '28%', borderRight: '1px solid #e2e8f0' }}>{row.label}</td>
                        <td style={{ padding: '9px 14px', color: '#1e293b' }}>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div>
              <h3 style={{ margin: '0 0 10px', fontSize: '0.78rem', fontWeight: '700', color: '#1e293b', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                Order Notes
              </h3>
              <p style={{ padding: '12px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', fontSize: '0.84rem', margin: 0, lineHeight: '1.5' }}>
                {order.notes}
              </p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '14px 20px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8fafc',
          borderRadius: '0 0 12px 12px',
          flexShrink: 0,
        }}>
          <button
            onClick={() => onDelete(order.id)}
            style={{
              padding: '9px 16px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fecaca'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
          >
            <Trash2 size={14} /> Delete Order
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              backgroundColor: '#e2e8f0',
              color: '#475569',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#cbd5e1'; e.currentTarget.style.color = '#1e293b'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewOrderModal;
